import React from "react"
import MultiSelect from "@/components/selectors/MultiSelect"
import { DROPDOWN_VARIANTS } from "@/components/common/Dropdown"
import Icon from "@/components/common/Icon/Icon"
import { BUTTON_TYPE, BUTTON_VARIANTS, ICONS } from "@/constants"
import Button from "@/components/common/Buttons/Button"
import { BREAKPOINTS } from "@/styles/themeSettings"
import { getCssSizeValue } from "@/utils/utils"
import styled from "styled-components"
import CustomDateInput from "@/components/articlesWithPagination/CustomDateInput"
import useDevice from "@/hooks/useDevice"
import FiltersReset from "@/components/articlesWithPagination/FiltersReset"
import useGlobals from "@/hooks/useGlobals"

const Top = styled.div`
	position: relative;
	width: 100%;
	display: flex;
	justify-content: space-between;
	column-gap: ${getCssSizeValue(14, BREAKPOINTS.DESKTOP)};

	.multi-select-category {
		margin-right: auto;
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		.multiselect {
			max-width: ${getCssSizeValue(250, BREAKPOINTS.TABLET)};
		}
	}

	.filtration {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 10px;
		height: min-content;

		@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
			flex-direction: row;
		}
	}

	.date-inputs {
		display: flex;
		width: 100%;
		gap: ${getCssSizeValue(12, BREAKPOINTS.MOBILE)};
		justify-content: space-between;

		@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
			justify-content: flex-start;
			gap: ${getCssSizeValue(12, BREAKPOINTS.TABLET)};
		}

		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			gap: ${getCssSizeValue(12, BREAKPOINTS.DESKTOP)};
		}
	}

	.buttons {
		display: none;

		@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
			display: flex;
			gap: ${getCssSizeValue(5, BREAKPOINTS.TABLET)};
		}

		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			gap: ${getCssSizeValue(16)};
		}

		.button {
			svg {
				height: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};

				@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
					height: ${getCssSizeValue(18, BREAKPOINTS.TABLET)};
				}
			}
		}
	}

	.separator {
		display: flex;
		align-items: center;
		font-size: 150%;
		margin: 0 -5px;
	}
`

const StyledSelectedFilters = styled.div`
	margin-top: ${getCssSizeValue(22, BREAKPOINTS.MOBILE)};
	display: flex;
	align-items: flex-start;
	width: 100%;

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		margin-top: unset;
		display: initial;
	}

	.selected-filters {
		display: flex;
		align-items: flex-start;
		flex-direction: column;
		text-transform: uppercase;
		color: ${({ theme }) => theme.colors.richBlack};
		font-weight: ${({ theme }) => theme.fontWeight.medium};
		line-height: normal !important;

		gap: ${getCssSizeValue(6, BREAKPOINTS.MOBILE)};

		@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
			gap: ${getCssSizeValue(12, BREAKPOINTS.TABLET)};
		}

		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			flex-direction: row;
			margin: unset;
		}
	}
`

const SelectedOptionWrapper = styled.div`
	position: relative;
	width: 65%;

	margin-left: calc(var(--paddingX) + 5px);

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		width: initial;
		margin-left: calc(var(--paddingX) + ${getCssSizeValue(10, BREAKPOINTS.TABLET)});
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin-left: ${getCssSizeValue(38)};
	}
`

const SelectedOption = styled.div`
	cursor: pointer;

	&:before {
		--size: ${getCssSizeValue(12, BREAKPOINTS.MOBILE)};
		content: "";
		background-color: ${({ theme }) => theme.colors.richBlack};
		position: absolute;
		top: ${getCssSizeValue(9, BREAKPOINTS.TABLET)};
		left: ${getCssSizeValue(-18)};
		width: var(--size);
		height: var(--size);

		@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
			top: ${getCssSizeValue(6, BREAKPOINTS.TABLET)};
			--size: ${getCssSizeValue(8, BREAKPOINTS.TABLET)};
		}

		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			top: ${getCssSizeValue(6)};
			--size: ${getCssSizeValue(8, BREAKPOINTS.DESKTOP)};
		}
	}
`

export default function Filtration({
	isGrid,
	setIsGrid,
	dropdownCategories,
	handleCategoryChange,
	selectedCategoriesFromQuery,
	startDateFromQuery,
	endDateFromQuery,
	handleStartDateChange,
	handleEndDateChange,
	handleResetFiltration,
	handleRemoveSingleFilter,
	isFilterApplied,
}) {
	const selectedValues = dropdownCategories.filter((category) => selectedCategoriesFromQuery.includes(category.urlParameter))
	const { isDesktop, isMobile } = useDevice()

	const chooseCategoriesSelectText = useGlobals((state) => state.chooseCategoriesSelectText)

	return (
		<>
			<Top>
				<div className="filtration">
					<MultiSelect
						variant={DROPDOWN_VARIANTS.TERTIARY}
						className="multi-select-category"
						options={dropdownCategories}
						selectedValues={selectedValues}
						placeholder={chooseCategoriesSelectText}
						onChange={handleCategoryChange}
						displaySelectedOptions={false}
						noBorder={false}
					/>
					<div className="date-inputs">
						<CustomDateInput value={startDateFromQuery} onChange={handleStartDateChange} />
						<div className="separator">-</div>
						<CustomDateInput value={endDateFromQuery} onChange={handleEndDateChange} />
						{!isMobile && <FiltersReset isFilterApplied={isFilterApplied} handleResetFiltration={handleResetFiltration} />}
					</div>
				</div>
				<div className="buttons">
					<Button
						onClick={() => setIsGrid(true)}
						type={BUTTON_TYPE.CHIP}
						variant={isGrid ? BUTTON_VARIANTS.PRIMARY : BUTTON_VARIANTS.TERTIARY}
					>
						{isDesktop ? "Grid" : <Icon name={ICONS.GRID} color={isGrid ? "white" : "richBlue"} />}
					</Button>
					<Button
						onClick={() => setIsGrid(false)}
						type={BUTTON_TYPE.CHIP}
						variant={!isGrid ? BUTTON_VARIANTS.PRIMARY : BUTTON_VARIANTS.TERTIARY}
					>
						{isDesktop ? "List" : <Icon name={ICONS.LIST} color={!isGrid ? "white" : "richBlue"} />}
					</Button>
				</div>
			</Top>
			<StyledSelectedFilters className="typo-strawford typo-strawford-35">
				<div className="selected-filters">
					{selectedValues.length !== 0 && (
						<SelectedOptionWrapper>
							<SelectedOption>
								{selectedValues.map((item, i) => {
									return (
										<span key={item?.id || i} onClick={() => handleRemoveSingleFilter("categories", item.urlParameter)}>
											{item.label}
											{i !== selectedValues.length - 1 ? <span>,&nbsp; </span> : null}
										</span>
									)
								})}
							</SelectedOption>
						</SelectedOptionWrapper>
					)}
					{(startDateFromQuery || endDateFromQuery) && (
						<SelectedOptionWrapper>
							<SelectedOption>
								<span onClick={() => handleRemoveSingleFilter("startDate")}>{startDateFromQuery}</span>-
								<span onClick={() => handleRemoveSingleFilter("endDate")}>{endDateFromQuery}</span>
							</SelectedOption>
						</SelectedOptionWrapper>
					)}
				</div>
				{isMobile && <FiltersReset isFilterApplied={isFilterApplied} handleResetFiltration={handleResetFiltration} />}
			</StyledSelectedFilters>
		</>
	)
}
